import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _dec, _class, _Table;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component, Children, isValidElement } from 'react';
import { safeCloneElement, omitProps } from '@instructure/ui-react-utils';
import { View } from '@instructure/ui-view';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { Head } from './Head';
import { Body } from './Body';
import { Row } from './Row';
import { ColHeader } from './ColHeader';
import { RowHeader } from './RowHeader';
import { Cell } from './Cell';
import { allowedProps, propTypes } from './props';
import TableContext from './TableContext';

/**
---
category: components
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let Table = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_Table = class Table extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  getHeaders() {
    const _Children$toArray = Children.toArray(this.props.children),
      _Children$toArray2 = _slicedToArray(_Children$toArray, 1),
      headChild = _Children$toArray2[0];
    if (!headChild || ! /*#__PURE__*/isValidElement(headChild)) return void 0;
    const _Children$toArray3 = Children.toArray(headChild.props.children),
      _Children$toArray4 = _slicedToArray(_Children$toArray3, 1),
      firstRow = _Children$toArray4[0];
    if (!firstRow || ! /*#__PURE__*/isValidElement(firstRow)) return void 0;
    return Children.map(firstRow.props.children, colHeader => {
      if (! /*#__PURE__*/isValidElement(colHeader)) return void 0;
      return colHeader.props.children;
    });
  }
  render() {
    const _this$props3 = this.props,
      margin = _this$props3.margin,
      layout = _this$props3.layout,
      caption = _this$props3.caption,
      children = _this$props3.children,
      hover = _this$props3.hover,
      styles = _this$props3.styles;
    const isStacked = layout === 'stacked';
    const headers = isStacked ? this.getHeaders() : void 0;
    return _jsx(TableContext.Provider, {
      value: {
        isStacked: isStacked,
        hover: hover,
        headers: headers
      },
      children: _jsxs(View, {
        ...View.omitViewProps(omitProps(this.props, Table.allowedProps), Table),
        as: isStacked ? 'div' : 'table',
        margin: margin,
        elementRef: this.handleRef,
        css: styles === null || styles === void 0 ? void 0 : styles.table,
        role: isStacked ? 'table' : void 0,
        "aria-label": isStacked ? caption : void 0,
        children: [!isStacked && _jsx("caption", {
          children: _jsx(ScreenReaderContent, {
            children: caption
          })
        }), Children.map(children, child => {
          if (/*#__PURE__*/isValidElement(child)) {
            return safeCloneElement(child, {
              key: child.props.name
            });
          }
          return child;
        })]
      })
    });
  }
}, _Table.displayName = "Table", _Table.componentId = 'Table', _Table.allowedProps = allowedProps, _Table.propTypes = propTypes, _Table.defaultProps = {
  children: null,
  hover: false,
  layout: 'auto'
}, _Table.Head = Head, _Table.Body = Body, _Table.Row = Row, _Table.ColHeader = ColHeader, _Table.RowHeader = RowHeader, _Table.Cell = Cell, _Table)) || _class);
export default Table;
export { Table };